import React, { memo, useEffect, useRef, useState } from 'react';
import { X, ZoomIn, ZoomOut } from 'lucide-react';

const distance = (touches) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

function GalleryLightbox({ image, enableZoom, onClose }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragY, setDragY] = useState(0);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const gesture = useRef({ startX: 0, startY: 0, startDistance: 0, startScale: 1, startOffset: { x: 0, y: 0 }, lastTap: 0 });

  useEffect(() => {
    if (!image) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setScale(1); setOffset({ x: 0, y: 0 }); setDragY(0);
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Tab') {
        const controls = [...(dialogRef.current?.querySelectorAll('button') || [])];
        if (!controls.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { window.removeEventListener('keydown', onKeyDown); document.body.style.overflow = previousOverflow; previousFocus?.focus?.(); };
  }, [image, onClose]);

  if (!image) return null;
  const toggleZoom = () => { setScale((value) => value > 1 ? 1 : 2); setOffset({ x: 0, y: 0 }); setDragY(0); };
  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      gesture.current.startDistance = distance(event.touches);
      gesture.current.startScale = scale;
    } else {
      gesture.current.startX = event.touches[0].clientX;
      gesture.current.startY = event.touches[0].clientY;
      gesture.current.startOffset = offset;
      const now = Date.now();
      if (enableZoom && now - gesture.current.lastTap < 280) toggleZoom();
      gesture.current.lastTap = now;
    }
  };
  const handleTouchMove = (event) => {
    if (event.touches.length === 2 && enableZoom) {
      event.preventDefault();
      setScale(Math.min(4, Math.max(1, gesture.current.startScale * distance(event.touches) / gesture.current.startDistance)));
    } else if (scale > 1) {
      setOffset({
        x: gesture.current.startOffset.x + event.touches[0].clientX - gesture.current.startX,
        y: gesture.current.startOffset.y + event.touches[0].clientY - gesture.current.startY
      });
    } else setDragY(Math.max(0, event.touches[0].clientY - gesture.current.startY));
  };
  const handleTouchEnd = () => {
    if (scale <= 1) setOffset({ x: 0, y: 0 });
    if (dragY > 100) onClose(); else setDragY(0);
  };

  return <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`Anteprima: ${image.alt}`} className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-black/95 p-4" onClick={onClose} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
    <button ref={closeRef} type="button" onClick={onClose} aria-label="Chiudi anteprima" className="absolute right-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"><X aria-hidden="true" /></button>
    {enableZoom && <button type="button" onClick={(event) => { event.stopPropagation(); toggleZoom(); }} aria-label={scale > 1 ? 'Riduci immagine' : 'Ingrandisci immagine'} className="absolute bottom-5 right-5 z-10 flex h-12 w-12 items-center justify-center rounded-full text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">{scale > 1 ? <ZoomOut aria-hidden="true" /> : <ZoomIn aria-hidden="true" />}</button>}
    <img src={image.src} alt={image.alt} width={image.width} height={image.height} decoding="async" draggable="false" onClick={(event) => event.stopPropagation()} onDoubleClick={enableZoom ? toggleZoom : undefined} className="max-h-[92svh] max-w-full select-none object-contain" style={{ transform: `translate3d(${offset.x}px, ${offset.y + dragY}px, 0) scale(${scale})`, touchAction: 'none', transition: dragY || scale > 1 ? 'none' : 'transform 300ms ease-out' }} />
  </div>;
}

export default memo(GalleryLightbox);